import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  primaryCta: string
  secondaryCta: string
  badges: string[]
  pillarsEyebrow: string
  pillarsTitle: string
  pillarsBody: string
  pillars: Pillar[]
  howEyebrow: string
  howTitle: string
  steps: Step[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'External & partner access',
    metaDescription:
      'Standards-based federation, brokered vendor PAM, and contractor lifecycle automation, for partners, suppliers, integrators, and OEMs.',
    heroEyebrow: 'Use cases · External & Partner Access',
    heroTitle: ['Partners in.', 'Flat-network risk out.'],
    heroBody:
      'Standards-based federation, brokered privileged access, and contractor lifecycle automation. Bring partners, suppliers, and integrators in, without inheriting the breach surface of their networks.',
    primaryCta: 'Start free trial →',
    secondaryCta: 'Talk with our experts',
    badges: [
      'SAML 2 / OIDC / SCIM',
      'Brokered vendor PAM',
      'Time-bound contractor identity',
    ],
    pillarsEyebrow: 'What you get',
    pillarsTitle: 'Working with outsiders, without the outsiders’ risk.',
    pillarsBody:
      'External and partner access is where most enterprises bleed risk silently. Monofor treats it as a first-class identity scenario, not a series of one-off VPN exceptions.',
    pillars: [
      {
        title: 'Federation on standards',
        description:
          'Bring partners, suppliers, and integrators in through SAML 2, OIDC, and SCIM. No shared credentials, no ad-hoc VPN handoffs.',
        bullets: [
          'SAML 2 / OIDC federation per partner',
          'SCIM-driven partner-managed provisioning',
          'Per-partner policy and audit isolation',
          'Just-in-time partner deprovisioning',
        ],
      },
      {
        title: 'No flat-network vendor access',
        description:
          'Third-party engineers, integrators, and OEMs get brokered, recorded, time-bound access through Monopam, not flat VPN connections.',
        bullets: [
          'Brokered RDP, SSH, web, and database sessions',
          'Full session recording with keystroke transcription',
          'Time-bound vendor access with hard expiry',
          'Per-vendor approval flow',
        ],
      },
      {
        title: 'Contractor and supplier lifecycle',
        description:
          'Time-bound identity for contractors and suppliers. Renewal flows go to the sponsor, not the help desk. Expiry fires automatically.',
        bullets: [
          'Hard-expiry on contractor accounts',
          'Sponsor-driven renewal workflow',
          'Per-vendor-org isolation in audit trail',
          'Auto-revoke on sponsor change',
        ],
      },
      {
        title: 'Customer-org delegated admin',
        description:
          'Let customer-org administrators manage their own users without touching your central console. Identity at scale without operational drag.',
        bullets: [
          'Customer-org tenancy',
          'Delegated admin with scoped privileges',
          'Per-org branded sign-in flow',
          'Per-org audit and reporting',
        ],
      },
    ],
    howEyebrow: 'How it works',
    howTitle: 'Federate. Broker. Expire.',
    steps: [
      {
        title: 'Federate the partner',
        description:
          'Stand up a SAML or OIDC connection. Add SCIM for partner-managed provisioning if needed.',
      },
      {
        title: 'Broker the access',
        description:
          'Route every privileged vendor session through Monopam. Vault any shared credentials. Time-bound. Recorded.',
      },
      {
        title: 'Operate without surprises',
        description:
          'Per-vendor audit trails, expiry-driven deprovisioning, and sponsor-routed renewals make partner access boring, which is the goal.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      {
        label: 'Single Sign-On',
        description: 'The federation protocol surface for partner connections.',
        href: '/sign/sso',
      },
      {
        label: 'Vendor Privileged Access',
        description: 'Invited, approval-gated, time-boxed vendor accounts in Monopam.',
        href: '/pam/vendor-access',
      },
      {
        label: 'Just-in-time access',
        description: 'Time-bound, approval-driven access for vendor tasks.',
        href: '/pam/jit-access',
      },
    ],
  },
  tr: {
    metaTitle: 'Dış ve iş ortağı erişimi',
    metaDescription:
      'Standartlara dayalı federasyon, aracılı tedarikçi PAM ve yüklenici yaşam döngüsü otomasyonu: iş ortakları, tedarikçiler, entegratörler ve OEM’ler için.',
    heroEyebrow: 'Kullanım senaryoları · Dış ve İş Ortağı Erişimi',
    heroTitle: ['İş ortakları içeri.', 'Düz ağ riski dışarı.'],
    heroBody:
      'Standartlara dayalı federasyon, aracılı ayrıcalıklı erişim ve yüklenici yaşam döngüsü otomasyonu. İş ortaklarını, tedarikçileri ve entegratörleri içeri alın, ağlarının ihlal yüzeyini devralmadan.',
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Uzmanlarımızla görüşün',
    badges: [
      'SAML 2 / OIDC / SCIM',
      'Aracılı tedarikçi PAM',
      'Süreli yüklenici kimliği',
    ],
    pillarsEyebrow: 'Neler elde edersiniz',
    pillarsTitle: 'Dış taraflarla çalışın, dış tarafların riski olmadan.',
    pillarsBody:
      'Dış ve iş ortağı erişimi, çoğu kurumun sessizce risk sızdırdığı yerdir. Monofor bunu birinci sınıf bir kimlik senaryosu olarak ele alır, tek seferlik VPN istisnaları dizisi olarak değil.',
    pillars: [
      {
        title: 'Standartlar üzerinde federasyon',
        description:
          'İş ortaklarını, tedarikçileri ve entegratörleri SAML 2, OIDC ve SCIM üzerinden içeri alın. Paylaşılan kimlik bilgisi yok, gelişigüzel VPN devirleri yok.',
        bullets: [
          'İş ortağı bazında SAML 2 / OIDC federasyonu',
          'SCIM ile iş ortağı yönetimli erişim sağlama',
          'İş ortağı bazında politika ve denetim izolasyonu',
          'İş ortağı erişiminin tam zamanında kaldırılması',
        ],
      },
      {
        title: 'Düz ağdan tedarikçi erişimine son',
        description:
          'Üçüncü taraf mühendisler, entegratörler ve OEM’ler, düz VPN bağlantıları yerine Monopam üzerinden aracılı, kayıtlı ve süreli erişim alır.',
        bullets: [
          'Aracılı RDP, SSH, web ve veritabanı oturumları',
          'Tuş vuruşu dökümüyle tam oturum kaydı',
          'Kesin süre dolumlu, süreli tedarikçi erişimi',
          'Tedarikçi bazında onay akışı',
        ],
      },
      {
        title: 'Yüklenici ve tedarikçi yaşam döngüsü',
        description:
          'Yükleniciler ve tedarikçiler için süreli kimlik. Yenileme akışları yardım masasına değil, sponsora gider. Süre dolumu otomatik devreye girer.',
        bullets: [
          'Yüklenici hesaplarında kesin süre dolumu',
          'Sponsor güdümlü yenileme iş akışı',
          'Denetim izinde tedarikçi kuruluşu bazında izolasyon',
          'Sponsor değişiminde otomatik geri alma',
        ],
      },
      {
        title: 'Müşteri kuruluşları için yetki devredilmiş yönetim',
        description:
          'Müşteri kuruluş yöneticilerinin, merkezi konsolunuza dokunmadan kendi kullanıcılarını yönetmesine izin verin. Operasyonel yük olmadan ölçekli kimlik.',
        bullets: [
          'Müşteri kuruluşu kiracılığı',
          'Kapsamı daraltılmış ayrıcalıklarla yetki devri',
          'Kuruluş bazında markalı oturum açma akışı',
          'Kuruluş bazında denetim ve raporlama',
        ],
      },
    ],
    howEyebrow: 'Nasıl çalışır',
    howTitle: 'Federe edin. Aracılayın. Süreye bağlayın.',
    steps: [
      {
        title: 'İş ortağını federe edin',
        description:
          'Bir SAML veya OIDC bağlantısı kurun. Gerekirse iş ortağı yönetimli erişim sağlama için SCIM ekleyin.',
      },
      {
        title: 'Erişimi aracılayın',
        description:
          'Her ayrıcalıklı tedarikçi oturumunu Monopam üzerinden yönlendirin. Paylaşılan kimlik bilgilerini kasaya alın. Süreli. Kayıtlı.',
      },
      {
        title: 'Sürprizsiz işletin',
        description:
          'Tedarikçi bazında denetim izleri, süre dolumuna bağlı erişim kaldırma ve sponsora yönlendirilen yenilemeler, iş ortağı erişimini sıradan hale getirir; hedef de zaten budur.',
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      {
        label: 'Tek Oturum Açma (SSO)',
        description: 'İş ortağı bağlantıları için federasyon protokol yüzeyi.',
        href: '/sign/sso',
      },
      {
        label: 'Tedarikçi Ayrıcalıklı Erişimi',
        description: 'Monopam’da davetli, onay kapılı, süreli tedarikçi hesapları.',
        href: '/pam/vendor-access',
      },
      {
        label: 'Just-in-time erişim',
        description: 'Tedarikçi görevleri için süreli, onaya dayalı erişim.',
        href: '/pam/jit-access',
      },
    ],
  },
}

type Props = { locale: string }

export default function ExternalPartnersPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/solutions/external-partners"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/contact' }}
          badges={c.badges}
        />
        <SolutionPillars
          eyebrow={c.pillarsEyebrow}
          title={<>{c.pillarsTitle}</>}
          body={c.pillarsBody}
          pillars={c.pillars}
        />
        <ProductHowItWorks
          eyebrow={c.howEyebrow}
          title={<>{c.howTitle}</>}
          steps={c.steps}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
